import React, { useState } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Select,
  Input,
  IconButton,
  Badge,
  Radio,
  RadioGroup,
  Stack,
  Divider,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Spinner,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiTrash2,
  FiPlay,
  FiAward,
  FiCheck,
} from 'react-icons/fi';
import { canvasAPI } from '../../../services/api';

const CriteriaTab = ({ students, loading, onEvaluate, onCertificatesCreated, defaultDuration = '40 horas', contextTitle = '', settings = null }) => {
  const [operator, setOperator] = useState('AND');
  const [conditions, setConditions] = useState([
    { type: 'final_grade', operator: '>=', value: 70, assignment_id: null }
  ]);
  const [evaluatedStudents, setEvaluatedStudents] = useState([]);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [courseName, setCourseName] = useState('');
  const [duration, setDuration] = useState('40 horas');
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [customCourseValues, setCustomCourseValues] = useState({});
  const [templateVariablesRequired, setTemplateVariablesRequired] = useState([]);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Cargar assignments al montar el componente
  React.useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      setLoadingAssignments(true);
      const response = await canvasAPI.getCourseAssignments();
      setAssignments(response.assignments || []);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las evaluaciones del curso',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingAssignments(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await canvasAPI.getInstructorTemplates();
      setTemplates(response || []);
      const defaultTemplate = response.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
        updateTemplateVariables(defaultTemplate);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Advertencia',
        description: 'No se pudieron cargar las plantillas de certificados',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const updateTemplateVariables = (template) => {
    if (!template || !template.variables_used || !template.variables_used.variables) {
      setTemplateVariablesRequired([]);
      return;
    }
    if (!settings?.custom_variables) {
      setTemplateVariablesRequired([]);
      return;
    }
    const courseVarsRequired = template.variables_used.variables
      .filter(varName => {
        const varConfig = settings.custom_variables[varName];
        return varConfig && varConfig.type === 'course';
      })
      .map(varName => ({
        name: varName,
        config: settings.custom_variables[varName]
      }));
    setTemplateVariablesRequired(courseVarsRequired);
  };

  const addCondition = () => {
    setConditions([
      ...conditions,
      { type: 'final_grade', operator: '>=', value: 70, assignment_id: null }
    ]);
  };

  const removeCondition = (index) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((_, i) => i !== index));
    }
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      [field]: value
    };
    setConditions(newConditions);
  };

  const handleEvaluate = async () => {
    try {
      setIsEvaluating(true);
      
      // Construir criterios
      const criteria = {
        operator: operator,
        conditions: conditions.map(c => ({
          type: c.type,
          operator: c.operator,
          value: parseFloat(c.value),
          assignment_id: c.assignment_id || undefined
        }))
      };

      const response = await canvasAPI.evaluateStudents(criteria);
      setEvaluatedStudents(response.eligible_students || []);
      
      toast({
        title: 'Evaluación completada',
        description: `${response.total_eligible} de ${response.total_students} estudiantes cumplen los criterios`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error evaluating students:', error);
      toast({
        title: 'Error',
        description: 'No se pudo evaluar los estudiantes',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleOpenCreateModal = () => {
    if (evaluatedStudents.length === 0) {
      toast({
        title: 'Sin estudiantes elegibles',
        description: 'Primero evalúa los criterios para ver qué estudiantes son elegibles',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setCourseName(contextTitle || '');
    setDuration(defaultDuration || '40 horas');
    setCustomCourseValues({});
    setSelectedTemplateId(null);
    setTemplateVariablesRequired([]);
    loadTemplates();
    onOpen();
  };

  const handleCreateCertificates = async () => {
    if (!courseName.trim()) {
      toast({
        title: 'Nombre del curso requerido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const missingVars = templateVariablesRequired.filter(
      v => !customCourseValues[v.name] || customCourseValues[v.name].trim() === ''
    );
    if (missingVars.length > 0) {
      toast({
        title: 'Variables requeridas faltantes',
        description: `Debes proporcionar valores para: ${missingVars.map(v => v.name).join(', ')}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsCreating(true);
      const studentIds = evaluatedStudents.map(s => s.user_id);

      const response = await canvasAPI.bulkCreateCertificates(
        studentIds,
        courseName,
        duration,
        selectedTemplateId,
        Object.keys(customCourseValues).length > 0 ? customCourseValues : null
      );

      const { job_id, total_students } = response;

      if (!job_id) {
        throw new Error('No se recibió job_id del servidor');
      }

      setCurrentJobId(job_id);
      setJobProgress({
        status: 'pending',
        processed: 0,
        total: total_students,
        created: 0,
        skipped: 0,
        failed: 0,
        percentage: 0,
      });

      toast({
        title: 'Procesando certificados',
        description: `Verificando ${total_students} estudiante(s). Esto puede tardar unos momentos...`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });

      startJobPolling(job_id);
    } catch (error) {
      console.error('Error creating certificates:', error);
      setIsCreating(false);
      setCurrentJobId(null);
      setJobProgress(null);

      if (error.response?.status === 400 && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object' && detail.missing_fields) {
          toast({
            title: '⚠️ Configuración incompleta',
            description: `${detail.message}\n\nCampos faltantes:\n• ${detail.missing_fields.join('\n• ')}`,
            status: 'warning',
            duration: 10000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Error de configuración',
            description: typeof detail === 'string' ? detail : detail.message || 'No se pudo crear los certificados',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Error',
          description: error.response?.data?.detail || error.message || 'No se pudo iniciar la creación de certificados',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const startJobPolling = (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const jobStatus = await canvasAPI.getCertificateJobStatus(jobId);

        setJobProgress({
          status: jobStatus.status,
          processed: jobStatus.progress.processed,
          total: jobStatus.progress.total,
          created: jobStatus.progress.created,
          skipped: jobStatus.progress.skipped,
          failed: jobStatus.progress.failed,
          percentage: jobStatus.progress.percentage,
        });

        if (['completed', 'failed', 'partial'].includes(jobStatus.status)) {
          clearInterval(pollInterval);
          setIsCreating(false);
          setCurrentJobId(null);
          setJobProgress(null);

          const { created, skipped, failed } = jobStatus.progress;

          if (jobStatus.status === 'failed') {
            toast({
              title: '❌ Error en la creación',
              description: jobStatus.error_message || 'Ocurrió un error al crear los certificados',
              status: 'error',
              duration: 10000,
              isClosable: true,
            });
          } else if (created === 0 && skipped > 0) {
            toast({
              title: 'ℹ️ Sin certificados nuevos',
              description: `Los ${skipped} estudiante(s) seleccionados ya tienen certificado emitido para este curso.`,
              status: 'info',
              duration: 8000,
              isClosable: true,
            });
          } else if (jobStatus.status === 'partial') {
            toast({
              title: '⚠️ Completado con advertencias',
              description: `${created} creados, ${skipped} ya tenían certificado, ${failed} fallidos`,
              status: 'warning',
              duration: 10000,
              isClosable: true,
            });
          } else {
            toast({
              title: '✅ Certificados creados',
              description: `${created} creado(s) exitosamente${skipped > 0 ? `, ${skipped} ya tenían certificado` : ''}`,
              status: 'success',
              duration: 7000,
              isClosable: true,
            });
          }

          setEvaluatedStudents([]);
          setCourseName('');
          setDuration('40 horas');
          onClose();

          if (onCertificatesCreated) {
            await onCertificatesCreated();
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        clearInterval(pollInterval);
        setIsCreating(false);
        setCurrentJobId(null);
        setJobProgress(null);
        toast({
          title: 'Error',
          description: 'No se pudo obtener el estado del procesamiento',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }, 2000);
  };

  const getOperatorLabel = (op) => {
    const labels = {
      '>=': 'Mayor o igual que',
      '>': 'Mayor que',
      '<=': 'Menor o igual que',
      '<': 'Menor que',
      '==': 'Igual a'
    };
    return labels[op] || op;
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* Criteria Builder */}
        <Card>
          <CardHeader>
            <Heading size="md">Constructor de Criterios</Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Define criterios automáticos para seleccionar estudiantes elegibles
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* Operator */}
              <FormControl>
                <FormLabel>Operador Lógico</FormLabel>
                <RadioGroup value={operator} onChange={setOperator}>
                  <Stack direction="row" spacing={4}>
                    <Radio value="AND">
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">AND (Y)</Text>
                        <Text fontSize="xs" color="gray.600">
                          Todas las condiciones deben cumplirse
                        </Text>
                      </VStack>
                    </Radio>
                    <Radio value="OR">
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">OR (O)</Text>
                        <Text fontSize="xs" color="gray.600">
                          Al menos una condición debe cumplirse
                        </Text>
                      </VStack>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <Divider />

              {/* Conditions */}
              <Box>
                <HStack justify="space-between" mb={3}>
                  <Text fontWeight="medium">Condiciones</Text>
                  <Button
                    size="sm"
                    leftIcon={<FiPlus />}
                    onClick={addCondition}
                    colorScheme="blue"
                    variant="ghost"
                  >
                    Agregar Condición
                  </Button>
                </HStack>

                <VStack spacing={4} align="stretch">
                  {conditions.map((condition, index) => (
                    <Card key={index} variant="outline" size="sm">
                      <CardBody>
                        <HStack spacing={3} align="end">
                          <FormControl flex={1}>
                            <FormLabel fontSize="sm">Tipo</FormLabel>
                            <Select
                              size="sm"
                              value={condition.type}
                              onChange={(e) => updateCondition(index, 'type', e.target.value)}
                            >
                              <option value="final_grade">Calificación Final</option>
                              <option value="assignment">Tarea Específica</option>
                            </Select>
                          </FormControl>

                          <FormControl flex={1}>
                            <FormLabel fontSize="sm">Operador</FormLabel>
                            <Select
                              size="sm"
                              value={condition.operator}
                              onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                            >
                              <option value=">=">{getOperatorLabel('>=')}</option>
                              <option value=">">{getOperatorLabel('>')}</option>
                              <option value="<=">{getOperatorLabel('<=')}</option>
                              <option value="<">{getOperatorLabel('<')}</option>
                              <option value="==">{getOperatorLabel('==')}</option>
                            </Select>
                          </FormControl>

                          <FormControl flex={1}>
                            <FormLabel fontSize="sm">Valor</FormLabel>
                            <Input
                              size="sm"
                              type="number"
                              value={condition.value}
                              onChange={(e) => updateCondition(index, 'value', e.target.value)}
                              min={0}
                              max={100}
                              step={0.1}
                            />
                          </FormControl>

                          {condition.type === 'assignment' && (
                            <FormControl flex={1}>
                              <FormLabel fontSize="sm">Evaluación</FormLabel>
                              <Select
                                size="sm"
                                value={condition.assignment_id || ''}
                                onChange={(e) => updateCondition(index, 'assignment_id', e.target.value)}
                                placeholder="Selecciona una evaluación"
                                isDisabled={loadingAssignments}
                              >
                                {assignments.map((assignment) => (
                                  <option key={assignment.id} value={assignment.id}>
                                    {assignment.name} ({assignment.points_possible || 0} pts)
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          <IconButton
                            icon={<FiTrash2 />}
                            aria-label="Eliminar condición"
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeCondition(index)}
                            isDisabled={conditions.length === 1}
                          />
                        </HStack>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              </Box>

              {/* Action Buttons */}
              <HStack spacing={3}>
                <Button
                  leftIcon={<FiPlay />}
                  colorScheme="blue"
                  onClick={handleEvaluate}
                  isLoading={isEvaluating}
                  loadingText="Evaluando..."
                >
                  Evaluar Criterios
                </Button>
                {evaluatedStudents.length > 0 && (
                  <Button
                    leftIcon={<FiAward />}
                    colorScheme="green"
                    onClick={handleOpenCreateModal}
                  >
                    Crear Certificados ({evaluatedStudents.length})
                  </Button>
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Results */}
        {evaluatedStudents.length > 0 && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">
                  Estudiantes Elegibles
                </Heading>
                <Badge colorScheme="green" fontSize="lg" px={3} py={1}>
                  {evaluatedStudents.length} estudiante(s)
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Estudiante</Th>
                      <Th>Email</Th>
                      <Th isNumeric>Calificación Final</Th>
                      <Th>Estado</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {evaluatedStudents.map((student) => (
                      <Tr key={student.user_id}>
                        <Td fontWeight="medium">{student.name}</Td>
                        <Td color="gray.600">{student.email || 'N/A'}</Td>
                        <Td isNumeric>
                          <Badge colorScheme="green">
                            {student.final_grade ? student.final_grade.toFixed(1) : 'N/A'}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <FiCheck color="green" />
                            <Text fontSize="sm" color="green.600">Elegible</Text>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Info Alert */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontSize="sm" fontWeight="medium">Cómo funciona:</Text>
            <VStack align="start" spacing={1} mt={2} fontSize="xs">
              <Text>1. Define una o más condiciones basadas en calificaciones</Text>
              <Text>2. Elige si deben cumplirse TODAS (AND) o AL MENOS UNA (OR)</Text>
              <Text>3. Evalúa para ver qué estudiantes son elegibles</Text>
              <Text>4. Crea certificados automáticamente para todos los elegibles</Text>
            </VStack>
          </Box>
        </Alert>
      </VStack>

      {/* Create Certificates Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Crear Certificados Automáticos</ModalHeader>
          <ModalCloseButton isDisabled={isCreating} />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info">
                <AlertIcon />
                Se crearán certificados para {evaluatedStudents.length} estudiante(s) que cumplen los criterios
              </Alert>

              {/* Progreso del job */}
              {isCreating && jobProgress && (
                <Box width="100%">
                  <Alert status="info" mb={3}>
                    <AlertIcon />
                    <VStack align="start" spacing={1} flex="1">
                      <Text fontWeight="bold">
                        {jobProgress.status === 'pending' && 'Iniciando...'}
                        {jobProgress.status === 'processing' && 'Procesando certificados...'}
                      </Text>
                      <Text fontSize="sm">
                        {jobProgress.processed} de {jobProgress.total} estudiantes procesados
                      </Text>
                      {jobProgress.status === 'processing' && (
                        <HStack fontSize="xs" color="gray.600" spacing={4}>
                          <Text>✓ Creados: {jobProgress.created}</Text>
                          <Text>⊘ Omitidos: {jobProgress.skipped}</Text>
                          <Text>✗ Fallidos: {jobProgress.failed}</Text>
                        </HStack>
                      )}
                    </VStack>
                  </Alert>
                  <Progress
                    value={jobProgress.percentage}
                    size="lg"
                    colorScheme="green"
                    hasStripe
                    isAnimated
                    mb={4}
                  />
                </Box>
              )}

              <FormControl isRequired isDisabled={isCreating}>
                <FormLabel>Nombre del Curso</FormLabel>
                <Input
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="Ej: Introducción a Python"
                />
              </FormControl>

              <FormControl isRequired isDisabled={isCreating}>
                <FormLabel>Duración</FormLabel>
                <Input
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="Ej: 40 horas"
                />
              </FormControl>

              <FormControl isDisabled={isCreating}>
                <FormLabel>Tipo de Certificado</FormLabel>
                {loadingTemplates ? (
                  <Spinner size="sm" />
                ) : templates.length > 0 ? (
                  <Select
                    value={selectedTemplateId || ''}
                    onChange={(e) => {
                      const newId = e.target.value ? parseInt(e.target.value) : null;
                      setSelectedTemplateId(newId);
                      if (newId) {
                        const tpl = templates.find(t => t.id === newId);
                        if (tpl) updateTemplateVariables(tpl);
                      } else {
                        setTemplateVariablesRequired([]);
                      }
                    }}
                    placeholder="Selecciona un tipo de certificado"
                  >
                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name} {tpl.is_default ? '(Predeterminado)' : ''}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No hay plantillas disponibles. Se usará la plantilla predeterminada.
                  </Text>
                )}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Selecciona el tipo de diploma (participación, aprobación, etc.)
                </Text>
              </FormControl>

              {/* Variables personalizadas requeridas por la plantilla */}
              {templateVariablesRequired.length > 0 && (
                <Box width="100%" p={4} borderWidth="1px" borderRadius="md" bg="purple.50">
                  <HStack mb={3}>
                    <Text fontSize="md" fontWeight="bold" color="purple.700">
                      Variables Requeridas para Esta Plantilla
                    </Text>
                    <Badge colorScheme="purple">Obligatorias</Badge>
                  </HStack>
                  <VStack spacing={3} align="stretch">
                    {templateVariablesRequired.map(({ name: varName, config }) => (
                      <FormControl key={varName} isDisabled={isCreating} isRequired>
                        <FormLabel fontSize="sm" color="purple.700">
                          {varName}
                          {config.description && (
                            <Text as="span" fontSize="xs" color="gray.600" ml={2} fontWeight="normal">
                              ({config.description})
                            </Text>
                          )}
                        </FormLabel>
                        <Input
                          size="sm"
                          value={customCourseValues[varName] || ''}
                          onChange={(e) => setCustomCourseValues({
                            ...customCourseValues,
                            [varName]: e.target.value
                          })}
                          placeholder={config.example || config.value || `Ingrese ${varName}`}
                          borderColor={
                            !customCourseValues[varName] || customCourseValues[varName].trim() === ''
                              ? 'red.300'
                              : 'gray.200'
                          }
                        />
                        {config.example && (
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Ejemplo: {config.example}
                          </Text>
                        )}
                      </FormControl>
                    ))}
                  </VStack>
                  <Alert status="info" mt={3} fontSize="xs">
                    <AlertIcon />
                    Estos valores se aplicarán a todos los certificados de este lote
                  </Alert>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isCreating}>
              {isCreating ? 'Procesando...' : 'Cancelar'}
            </Button>
            <Button
              colorScheme="green"
              onClick={handleCreateCertificates}
              isLoading={isCreating}
              loadingText={jobProgress ? `${jobProgress.percentage}%` : 'Iniciando...'}
              isDisabled={isCreating}
            >
              Crear Certificados
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CriteriaTab;
